import AsyncStorage from '@react-native-async-storage/async-storage';
import { captureException } from '@/utils/sentry';
import { useCallback, useEffect, useState } from 'react';

const LAST_USED_CREDIT_REQUEST_REASON_KEY_PREFIX =
  'lastUsedCreditRequestReason:';

/**
 * In-memory mirror of the AsyncStorage-backed value, keyed by supplier.
 * Reads within the same app session must go through this first: the
 * AsyncStorage write in recordReasonUsed is fire-and-forget, so a form
 * mounted for the next item in a multi-item batch can otherwise read
 * stale storage before the previous item's write has landed.
 */
const lastUsedReasonBySupplier = new Map<string, string>();

/**
 * Test-only: clears the in-memory cache so it doesn't leak state between
 * test cases that reuse the same supplier UUID.
 */
export const resetLastUsedCreditRequestReasonCache = () => {
  lastUsedReasonBySupplier.clear();
};

/**
 * Remembers the last credit request reason selected for a given supplier,
 * persisted locally so it can be offered as the default the next time a
 * credit request is filed for that supplier. Each supplier gets its own
 * AsyncStorage key so unrelated suppliers can't race on a shared value.
 */
export const useLastUsedCreditRequestReason = (supplierUUID: string) => {
  const [lastUsedReasonUUID, setLastUsedReasonUUID] = useState<string | null>(
    lastUsedReasonBySupplier.get(supplierUUID) ?? null,
  );

  useEffect(() => {
    const cached = lastUsedReasonBySupplier.get(supplierUUID);
    if (cached) {
      setLastUsedReasonUUID(cached);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const stored = await AsyncStorage.getItem(
          `${LAST_USED_CREDIT_REQUEST_REASON_KEY_PREFIX}${supplierUUID}`,
        );
        // A recordReasonUsed call synchronous with this read (e.g. the user
        // picked a reason while this read was in flight) already put the
        // authoritative value in the cache. Discard this read rather than
        // clobbering it with what may be a stale result.
        if (cancelled || lastUsedReasonBySupplier.has(supplierUUID)) {
          return;
        }
        if (stored) {
          lastUsedReasonBySupplier.set(supplierUUID, stored);
        }
        setLastUsedReasonUUID(stored);
      } catch (e) {
        captureException(e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [supplierUUID]);

  const recordReasonUsed = useCallback(
    (reasonUUID: string) => {
      lastUsedReasonBySupplier.set(supplierUUID, reasonUUID);
      setLastUsedReasonUUID(reasonUUID);
      (async () => {
        try {
          await AsyncStorage.setItem(
            `${LAST_USED_CREDIT_REQUEST_REASON_KEY_PREFIX}${supplierUUID}`,
            reasonUUID,
          );
        } catch (e) {
          captureException(e);
        }
      })();
    },
    [supplierUUID],
  );

  return { lastUsedReasonUUID, recordReasonUsed };
};
